/**
 * Message Templates for Wedding Invitations
 * 
 * This file contains various message templates that can be used
 * for creating personalized wedding invitation messages.
 */

// Import venue data
import { VENUE_INFO } from './eventsData.js';

// Utility function to get dates for specific redirect paths
const getDatesByRedirectPath = (redirectPath) => {
  const pathMappings = {
    '/maagiennda': '24th & 25th Nov 2025', // Main agenda - Sangeet and Wedding days
    '/cweerdedmionngy': '25th Nov 2025', // Wedding day only
    '/rfeucnecpttiioonn': '25th Nov 2025', // Reception day only
    '/swaendgdeientg': '24th & 25th Nov 2025', // Sangeet and Wedding days
    '/wiendvdiitnagtion': '22nd, 24th & 25th Nov 2025', // All wedding events
    '/fllagnd8293kxm': '22nd, 24th & 25th Nov 2025', // All events
    '/tmlne4739hjk': '22nd, 24th & 25th Nov 2025', // All events
    '/cmbndgnd8392plw': '22nd, 24th & 25th Nov 2025', // All events
    '/sngwdrcp8472mjk': '24th & 25th Nov 2025' // Main events (Sangeet, Wedding, Reception)
  };
  return pathMappings[redirectPath] || '22nd, 24th & 25th Nov 2025';
};

// Get formatted venue information
const getVenueInfo = () => {
  return `${VENUE_INFO.name}, ${VENUE_INFO.address.join(', ')}`; // Full address
};

export const MESSAGE_TEMPLATES = {
  // Template 1: Standard wedding invitation
  weddingInvitationStandard: {
    id: 'wedding_standard',
    name: 'Wedding Invitation (Standard)',
    template: (guestName, password, fullUrl, redirectPath) => {
      const weddingDate = getDatesByRedirectPath(redirectPath);
      const venue = getVenueInfo();
      return `Dear ${guestName},

We are pleased to invite you to the wedding celebration of our daughter

🌸 Radhika 🌸
    with 
🌸 Vivek 🌸

Your presence would be a blessing as we celebrate this joyous occasion.

━━━━━━━━━━━━━━━━━━━━━━━━━

📅 *Wedding Dates:* ${weddingDate}
📍 *Venue:* ${venue}
✉️ *Invitation Link:*
${fullUrl}
🔑 *Password:* Please use your phone number (${password}) as the password for your invitation link

━━━━━━━━━━━━━━━━━━━━━━━━━

We look forward to sharing this special moment with you.

With warm regards,
*The Patil Family*`;
    }
  },

  // Template 2: WhatsApp/SMS friendly
  weddingInvitationWhatsApp: {
    id: 'wedding_whatsapp',
    name: 'WhatsApp/SMS Friendly',
    template: (guestName, password, fullUrl, redirectPath) => {
      const weddingDate = getDatesByRedirectPath(redirectPath);
      const venue = getVenueInfo();
      return `Dear ${guestName}!

We're excited to invite you to our daughter's wedding! 🎉

🌸 Radhika 🌸
    with 
🌸 Vivek 🌸
_______________________________________________

📅 Date: ${weddingDate}
📍 Venue: ${venue}

✉️ Invitation link:
${fullUrl}
🔑 Password: Please use your phone number (${password}) as the password for your invitation link
_______________________________________________
Your presence would make our celebration complete! 🌟

With love,
The Patil Family`;
    }
  },

  // Template 3: Email friendly
  weddingInvitationEmail: {
    id: 'wedding_email',
    name: 'Email Friendly',
    template: (guestName, password, fullUrl, redirectPath) => {
      const weddingDate = getDatesByRedirectPath(redirectPath);
      const venue = getVenueInfo();
      return `Dear ${guestName},

I hope this message finds you well.

We are delighted to invite you to the wedding celebration of our daughter

🌸 Radhika 🌸
    with 
🌸 Vivek 🌸
_______________________________________________

WEDDING DETAILS:
📅 Date: ${weddingDate}
📍 Venue: ${venue}

INVITATION ACCESS:
✉️ Invitation Link:
${fullUrl}
🔑 Password: Please use your phone number (${password}) as the password for your invitation link
_______________________________________________
Your presence would be a wonderful addition to our celebration. Please visit our wedding website for complete details about the ceremonies and festivities.

We look forward to celebrating with you.

Warm regards,
The Patil Family

P.S. Please save the date - we would love to have you join us! 🎉`;
    }
  },

  // Template 4: Vivek's Family Invitation
  weddingInvitationVivekFamily: {
    id: 'wedding_vivek_family',
    name: 'Vivek Family Invitation',
    template: (guestName, password, fullUrl, redirectPath) => {
      const weddingDate = getDatesByRedirectPath(redirectPath);
      const venue = getVenueInfo();
      return `Dear ${guestName},

With immense joy and pride, we invite you to witness the wedding of our beloved son

🌸 Vivek 🌸
    with 
🌸 Radhika 🌸

Your blessings and presence would honor us on this auspicious day.

━━━━━━━━━━━━━━━━━━━━━━━━━

📅 Wedding Dates: ${weddingDate}
📍 Venue: ${venue}
✉️ Invitation Link:
${fullUrl}
🔑 Password: Please use your phone number (${password}) as the password for your invitation link

━━━━━━━━━━━━━━━━━━━━━━━━━

We eagerly await your gracious presence.

With warm regards,
*The Jain Family*`;
    }
  },

  // Template 5: Traditional/Formal
  weddingInvitationTraditional: {
    id: 'wedding_traditional',
    name: 'Traditional Formal',
    template: (guestName, password, fullUrl, redirectPath) => {
      const weddingDate = getDatesByRedirectPath(redirectPath);
      const venue = getVenueInfo();
      return `Respected ${guestName},

It is with great pleasure and honor that we invite you to the sacred wedding ceremony of

🌸 Radhika Patil 🌸
          &
🌸 Vivek Jain 🌸

Your esteemed presence and blessings are humbly requested for this auspicious occasion.

━━━━━━━━━━━━━━━━━━━━━━━━━

📅 Ceremony Dates: ${weddingDate}
📍 Venue: ${venue}
✉️ Digital Invitation:
${fullUrl}
🔑 Access Code: Please use your phone number (${password}) as the password for your invitation link

━━━━━━━━━━━━━━━━━━━━━━━━━

We would be deeply honored by your presence and blessings.

With utmost respect,
*The Patil & Jain Families*`;
    }
  },

  // Template 6: Casual/Friends
  weddingInvitationCasual: {
    id: 'wedding_casual',
    name: 'Casual Friends',
    template: (guestName, password, fullUrl, redirectPath) => {
      const weddingDate = getDatesByRedirectPath(redirectPath);
      const venue = getVenueInfo();
      return `Hey ${guestName}! 😄

You're invited to the biggest party of the year! 🎉🎆

Our girl Radhika is getting married to Vivek! 👰🤵

🌸 Radhika & Vivek 🌸

Come join us for an amazing celebration filled with fun, food, music, and lots of dancing! 💃🎵

━━━━━━━━━━━━━━━━━━━━━━━━━

📅 When: ${weddingDate}
📍 Where: ${venue}
✉️ All the details: 
${fullUrl}
🔑 Login with: Please use your phone number (${password}) as the password for your invitation link

━━━━━━━━━━━━━━━━━━━━━━━━━

Don't miss out on the fun - we need you there! 🥳

Love and hugs,
The Patil Family 😘`;
    }
  }


};


// Default template
export const DEFAULT_TEMPLATE = MESSAGE_TEMPLATES.weddingInvitationStandard;

// Get all template options for dropdown
export const getTemplateOptions = () => {
  return Object.values(MESSAGE_TEMPLATES).map(template => ({
    value: template.id,
    label: template.name
  }));
};

// Get template by ID
export const getTemplateById = (templateId) => {
  return Object.values(MESSAGE_TEMPLATES).find(template => template.id === templateId) || DEFAULT_TEMPLATE;
};